import {
  arrowHead,
  arrowHeadDimensions,
  DEFAULT_ARROW_STROKE_WIDTH,
  type ArrowHeadStyle
} from './arrowHeadGeometry.ts';
import type { ReactionArrowAnnotationKind, ReactionArrowOverlay } from '../chemistry/reactionParticipant.ts';

// #395 - one source of truth for the geometry of a scheme (reaction) arrow drawn as a canvas
// annotation, so the live vector layer (PaperVectorLayer) and any future string export build the
// exact same shape. Everything is computed in the caller's coordinate space (pixels on the
// canvas); the caller strokes/fills the primitives and owns the colour.
//
// A reaction arrow runs from `from` to `to`. Each kind is expressed as a small list of
// primitives:
//   - `polyline`  open, stroked, never filled (shafts, barbs, open heads, overlay marks)
//   - `path`      open, stroked (wavy shaft, rearrangement loop)
//   - `polygon`   filled outline; `head` polygons must not be stroked

export interface ReactionArrowPoint {
  readonly x: number;
  readonly y: number;
}

export type ReactionArrowPrimitive =
  | { readonly t: 'polyline'; readonly points: readonly ReactionArrowPoint[]; readonly dashed?: boolean }
  | { readonly t: 'path'; readonly d: string; readonly dashed?: boolean }
  | {
      readonly t: 'polygon';
      readonly points: readonly ReactionArrowPoint[];
      readonly filled: boolean;
      readonly head?: ArrowHeadStyle;
    };

// Spacing and shaft decoration remain independent of the shared head proportions.
const RAIL_GAP = 2;
const EQUIL_GAP = 4.5;
const TICK_HALF = 5.5; // dipole tick + overlay marks
const WAVE_AMP = 4;
const WAVE_SEG = 9; // one half-wave along the shaft

interface Axis {
  readonly ux: number;
  readonly uy: number;
  readonly px: number;
  readonly py: number;
  readonly len: number;
}

function axis(from: ReactionArrowPoint, to: ReactionArrowPoint): Axis {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  return { ux, uy, px: -uy, py: ux, len };
}

// `p` moved `alongU` px down the arrow axis and `alongP` px along its perpendicular.
function offset(p: ReactionArrowPoint, a: Axis, alongU: number, alongP: number): ReactionArrowPoint {
  return { x: p.x + a.ux * alongU + a.px * alongP, y: p.y + a.uy * alongU + a.py * alongP };
}

function line(from: ReactionArrowPoint, to: ReactionArrowPoint, dashed?: boolean): ReactionArrowPrimitive {
  return dashed ? { t: 'polyline', points: [from, to], dashed: true } : { t: 'polyline', points: [from, to] };
}

function wavyShaft(from: ReactionArrowPoint, to: ReactionArrowPoint, a: Axis): ReactionArrowPrimitive {
  const segments = Math.max(2, Math.round(a.len / WAVE_SEG));
  const step = a.len / segments;
  let d = `M ${round(from.x)} ${round(from.y)}`;
  for (let i = 0; i < segments; i += 1) {
    const mid = offset(from, a, step * (i + 0.5), WAVE_AMP * (i % 2 === 0 ? 1 : -1));
    const end = offset(from, a, step * (i + 1), 0);
    d += ` Q ${round(mid.x)} ${round(mid.y)} ${round(end.x)} ${round(end.y)}`;
  }
  return { t: 'path', d };
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

function overlayMarks(
  from: ReactionArrowPoint,
  to: ReactionArrowPoint,
  a: Axis,
  overlay: ReactionArrowOverlay
): ReactionArrowPrimitive[] {
  const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
  if (overlay === 'single-bar') {
    return [{ t: 'polyline', points: [offset(mid, a, 0, TICK_HALF), offset(mid, a, 0, -TICK_HALF)] }];
  }
  if (overlay === 'slash') {
    return [-2.5, 2.5].map((along) => ({
      t: 'polyline',
      points: [offset(mid, a, along - TICK_HALF * 0.4, TICK_HALF), offset(mid, a, along + TICK_HALF * 0.4, -TICK_HALF)]
    }));
  }
  // 'x'
  return [
    { t: 'polyline', points: [offset(mid, a, -TICK_HALF, TICK_HALF), offset(mid, a, TICK_HALF, -TICK_HALF)] },
    { t: 'polyline', points: [offset(mid, a, -TICK_HALF, -TICK_HALF), offset(mid, a, TICK_HALF, TICK_HALF)] }
  ];
}

function shaftPrimitives(
  from: ReactionArrowPoint,
  to: ReactionArrowPoint,
  a: Axis,
  arrowKind: ReactionArrowAnnotationKind,
  strokeWidth: number,
  zoom: number
): ReactionArrowPrimitive[] {
  const forward = arrowHead(to, { x: a.ux, y: a.uy }, 'solid', strokeWidth);
  const backward = arrowHead(from, { x: -a.ux, y: -a.uy }, 'solid', strokeWidth);
  const half = (tip: ReactionArrowPoint, reversed = false) =>
    arrowHead(
      tip,
      { x: a.ux * (reversed ? -1 : 1), y: a.uy * (reversed ? -1 : 1) },
      'half',
      strokeWidth,
      -1,
      strokeWidth / zoom
    );
  switch (arrowKind) {
    case 'forward':
    case 'vertical-up':
      return [line(from, forward.base), forward.primitive];

    case 'dashed':
      return [line(from, forward.base, true), forward.primitive];

    case 'wavy':
      return [wavyShaft(from, forward.base, axis(from, forward.base)), forward.primitive];

    case 'resonance':
      return [line(backward.base, forward.base), forward.primitive, backward.primitive];

    case 'retrosynthetic': {
      const head = arrowHead(to, { x: a.ux, y: a.uy }, 'open', strokeWidth, -1, strokeWidth / zoom);
      // Keep both rails inside the open head even at the smallest stroke width.
      const gap = Math.min(RAIL_GAP, arrowHeadDimensions(strokeWidth).halfWidth * 0.5);
      return [
        line(offset(from, a, 0, gap), head.railEnd(gap)),
        line(offset(from, a, 0, -gap), head.railEnd(-gap)),
        head.primitive
      ];
    }

    // ⇌ : the upper half-arrow points forward (barb on the outer/upper side), the lower one
    // points back. `-p` is the upper side.
    case 'equilibrium': {
      const upperTip = offset(to, a, 0, -EQUIL_GAP);
      const lowerTip = offset(from, a, 0, EQUIL_GAP);
      const upper = half(upperTip);
      const lower = half(lowerTip, true);
      return [
        line(offset(from, a, 0, -EQUIL_GAP), upper.base),
        upper.primitive,
        line(offset(to, a, 0, EQUIL_GAP), lower.base),
        lower.primitive
      ];
    }

    // Same, but the back-pointing (lower) arrow is short -- "equilibrium lies to the right".
    case 'equilibrium-unequal': {
      const upperTip = offset(to, a, 0, -EQUIL_GAP);
      const lowerTip = offset(from, a, a.len * 0.62, EQUIL_GAP);
      const upper = half(upperTip);
      const lower = half(lowerTip, true);
      return [
        line(offset(from, a, 0, -EQUIL_GAP), upper.base),
        upper.primitive,
        line(offset(to, a, 0, EQUIL_GAP), lower.base),
        lower.primitive
      ];
    }

    case 'vertical-equilibrium': {
      // Two full antiparallel arrows, offset far enough that the solid heads clear each other.
      const gap = arrowHeadDimensions(strokeWidth).halfWidth + 1.5;
      const topTip = offset(to, a, 0, gap);
      const bottomTip = offset(from, a, 0, -gap);
      const upper = arrowHead(topTip, { x: a.ux, y: a.uy }, 'solid', strokeWidth);
      const lower = arrowHead(bottomTip, { x: -a.ux, y: -a.uy }, 'solid', strokeWidth);
      return [
        line(offset(from, a, 0, gap), upper.base),
        upper.primitive,
        line(offset(to, a, 0, -gap), lower.base),
        lower.primitive
      ];
    }

    case 'rearrangement': {
      // A straight shaft with a compact curl over it near the middle -- the "intramolecular"
      // cue. The curl is a near-full arc; its radius scales with the arrow but is capped so it
      // stays legible at glyph size.
      const r = Math.min(8, Math.max(4.5, a.len * 0.15));
      const start = offset(from, a, a.len * 0.5 - r * 0.55, 0);
      const end = offset(from, a, a.len * 0.5 + r * 0.55, 0);
      // large-arc + sweep 0 draws the long way round, bulging to -p (the upper side).
      const curl = `M ${round(start.x)} ${round(start.y)} A ${round(r)} ${round(r)} 0 1 0 ${round(end.x)} ${round(end.y)}`;
      return [line(from, forward.base), { t: 'path', d: curl }, forward.primitive];
    }

    case 'dipole': {
      const tickCentre = offset(from, a, a.len * 0.16, 0);
      return [
        line(from, forward.base),
        forward.primitive,
        { t: 'polyline', points: [offset(tickCentre, a, 0, TICK_HALF), offset(tickCentre, a, 0, -TICK_HALF)] }
      ];
    }
  }
}

export function reactionArrowPrimitives(
  from: ReactionArrowPoint,
  to: ReactionArrowPoint,
  arrowKind: ReactionArrowAnnotationKind,
  overlay?: ReactionArrowOverlay,
  strokeWidth = DEFAULT_ARROW_STROKE_WIDTH,
  zoom = 1
): readonly ReactionArrowPrimitive[] {
  const a = axis(from, to);
  const primitives = shaftPrimitives(from, to, a, arrowKind, strokeWidth, zoom);
  return overlay ? [...primitives, ...overlayMarks(from, to, a, overlay)] : primitives;
}
